import React, { useState } from 'react';
import { Card, CardContent } from './card';
import RoamIcon from './RoamIcon';
import { Edit2, Save, X, ExternalLink, Tag, EyeOff, Star } from 'lucide-react';
import './TweetCard.css';
import { useStagingTray } from '../../context/StagingTrayContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useSkipCategories } from '../../context/SkipCategoriesContext';
import FavoriteStar from './FavoriteStar';
import SkipCategoriesButton from './SkipCategoriesButton';
import SkipCategoriesModal from './SkipCategoriesModal';
import TagManager from './TagManager';
import { InkQuoteApi } from '../../services/inkquoteApi';

const RoamQuoteCard = ({ quote, viewMode = null, onUpdateQuote }) => {
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const { addItem, removeItem, isInTray } = useStagingTray();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isSkipped } = useSkipCategories();
  const inTray = isInTray(quote);
  const quoteIsSkipped = isSkipped(quote.id);
  const longPressTimer = React.useRef(null);
  const [isLongPressing, setIsLongPressing] = React.useState(false);
  const titleRef = React.useRef(null);
  const touchStart = React.useRef({ x: 0, y: 0, time: 0 });
  const quoteContent = quote.text || quote.content || '';
  const author = quote.author || quote.user?.name || 'Unknown Author';
  const createdAt = quote.created_at || new Date().toISOString();

  const [editedContent, setEditedContent] = useState(quoteContent);
  const [editedAuthor, setEditedAuthor] = useState(author);

  // Parse metadata - handle both old and new format
  let metadata = {};
  try {
    metadata = typeof quote.metadata === 'string' ? JSON.parse(quote.metadata) : quote.metadata || {};
  } catch (e) {
    metadata = {};
  }

  const [quoteTags, setQuoteTags] = useState(metadata.tags || []);
  const pageTitle = metadata.page_title || '';
  const [editedPageTitle, setEditedPageTitle] = useState(pageTitle);
  const originalId = metadata.original_id;
  const baseSourceUrl = metadata.source_url || '';
  const createTime = metadata.create_time; // Time in milliseconds

  // Use the actual creation time from Roam metadata if available, otherwise use date field
  const actualCreatedAt = createTime ? new Date(createTime).toISOString() : createdAt;

  // Extract author from page title if available (e.g., "Book/Title" -> no author, "Article/Title" -> no author)
  // For now, fallback to a generic source since author is null
  const displayAuthor = author;

  // Construct block-specific URL using original_id
  const blockUrl = originalId && baseSourceUrl
    ? baseSourceUrl.replace(/\/page\/[^/]+$/, `/page/${originalId}`)
    : baseSourceUrl;

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Detect if title text is clamped and add class for fade effect
  React.useEffect(() => {
    if (titleRef.current && !isEditing) {
      const element = titleRef.current;
      // Check if the element's scrollHeight is greater than clientHeight
      // This indicates the text is being clamped
      const isClamped = element.scrollHeight > element.clientHeight;

      if (isClamped) {
        element.classList.add('is-clamped');
      } else {
        element.classList.remove('is-clamped');
      }
    }
  }, [pageTitle, isEditing]);

  const handleSave = async () => {
    try {
      if (onUpdateQuote) {
        await onUpdateQuote(quote._inkquote_id, {
          content: editedContent,
          author: editedAuthor
        });
      } else {
        // Fallback to direct API call if no callback provided
        await InkQuoteApi.updateQuote(quote._inkquote_id, {
          content: editedContent,
          author: editedAuthor
        });
        window.location.reload(); // Temporary - will improve in Priority 2
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save quote:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedContent(quoteContent);
    setEditedAuthor(author);
    setIsEditing(false);
  };

  const renderQuoteText = (text) => {
    if (!text) return null;

    const lines = text.split('\n');

    return lines.map((line, lineIndex) => {
      if (line.startsWith('>')) {
        const quoteContent = line.substring(1).trim();
        return (
          <blockquote
            key={`line-${lineIndex}`}
            className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 italic text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded my-2"
          >
            {parseRoamMarkdown(quoteContent)}
          </blockquote>
        );
      }

      return <div key={`line-${lineIndex}`}>{parseRoamMarkdown(line)}</div>;
    });
  };

  const parseRoamMarkdown = (text) => {
    if (!text) return null;

    // Split text by markdown patterns while preserving the markers (including multiline support)
    const parts = text.split(/(\*\*[^*]+\*\*|__[^_\n]+__|`[^`]+`|\[\[[^\]]+\]\]|#\[\[[^\]]+\]\]|#\w+|\{\{[^}]+\}\}|!\[\]\([^)]+\)|https?:\/\/[^\s]+)/g);

    return parts.map((part, index) => {
      if (!part) return null;

      // Bold: **text**
      if (part.startsWith('**') && part.endsWith('**')) {
        const content = part.slice(2, -2);
        return <strong key={index}>{content}</strong>;
      }

      // Italics: __text__ (handle multiline)
      if (part.startsWith('__') && part.endsWith('__')) {
        const content = part.slice(2, -2);
        // Handle line breaks within italic text
        return (
          <em key={index}>
            {content.split('\n').map((line, lineIndex) => (
              <span key={lineIndex}>
                {line}
                {lineIndex < content.split('\n').length - 1 && <br />}
              </span>
            ))}
          </em>
        );
      }

      // Inline code: `text`
      if (part.startsWith('`') && part.endsWith('`')) {
        const content = part.slice(1, -1);
        return (
          <code
            key={index}
            className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono"
          >
            {content}
          </code>
        );
      }

      // Page references: [[text]]
      if (part.startsWith('[[') && part.endsWith(']]')) {
        const content = part.slice(2, -2);
        return (
          <span
            key={index}
            className="text-blue-600 dark:text-blue-400 font-medium cursor-pointer hover:underline"
            title={`Page: ${content}`}
          >
            [[{content}]]
          </span>
        );
      }

      // Tag references: #[[text]] or #hashtag
      if (part.startsWith('#[[') && part.endsWith(']]')) {
        const content = part.slice(3, -2);
        return (
          <span
            key={index}
            className="text-purple-600 dark:text-purple-400 font-medium cursor-pointer hover:underline"
            title={`Tag: ${content}`}
          >
            #{content}
          </span>
        );
      }

      // Simple hashtags: #word
      if (part.match(/^#\w+$/)) {
        const content = part.slice(1);
        return (
          <span
            key={index}
            className="text-purple-600 dark:text-purple-400 font-medium cursor-pointer hover:underline"
            title={`Tag: ${content}`}
          >
            #{content}
          </span>
        );
      }

      // Block embeds/queries: {{text}}
      if (part.startsWith('{{') && part.endsWith('}}')) {
        const content = part.slice(2, -2);
        
        // Handle video embeds: {{[[video]]: URL}}
        const videoMatch = content.match(/^\[\[video\]\]:\s*(.+)$/);
        if (videoMatch) {
          const videoUrl = videoMatch[1];
          return (
            <div key={index} className="my-2">
              <video 
                controls 
                className="max-w-full h-auto rounded"
                style={{ maxHeight: '400px' }}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="text-xs text-gray-500 mt-1">
                Video: {videoUrl.split('/').pop()?.split('?')[0] || 'video'}
              </div>
            </div>
          );
        }
        
        // Handle filename references: {{=: |filename}}
        const filenameMatch = content.match(/^=:\s*\|(.+)$/);
        if (filenameMatch) {
          const filename = filenameMatch[1];
          return (
            <span
              key={index}
              className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded text-xs font-mono"
              title={`Filename: ${filename}`}
            >
              📄 {filename}
            </span>
          );
        }
        
        // Default block reference styling
        return (
          <span
            key={index}
            className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-sm"
            title={`Block reference: ${content}`}
          >
            {content}
          </span>
        );
      }

      // Image syntax: ![](URL)
      if (part.match(/^!\[\]\(.+\)$/)) {
        const imageUrl = part.slice(4, -1); // Remove ![]( and )
        return (
          <div key={index} className="my-2">
            <img 
              src={imageUrl} 
              alt=""
              className="max-w-full h-auto rounded"
              style={{ maxHeight: '400px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="text-xs text-gray-500 mt-1" style={{ display: 'none' }}>
              Image failed to load: {imageUrl.split('/').pop()?.split('?')[0] || 'image'}
            </div>
          </div>
        );
      }

      // URLs
      if (part.match(/^https?:\/\//)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            {part}
          </a>
        );
      }

      // Handle line breaks in regular text
      if (part.includes('\n')) {
        return (
          <span key={index}>
            {part.split('\n').map((line, lineIndex) => (
              <span key={lineIndex}>
                {line}
                {lineIndex < part.split('\n').length - 1 && <br />}
              </span>
            ))}
          </span>
        );
      }

      return part;
    });
  };

  const handleLongPressStart = (e) => {
    // Skip if clicking interactive elements, editing, or not in manage mode
    if (e.target.closest('a, button, form, textarea, input') || viewMode !== 'manage' || isEditing) return;

    // Record initial touch position and time
    const touch = e.touches ? e.touches[0] : e;
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    setIsLongPressing(true);

    longPressTimer.current = setTimeout(() => {
      setShowActions(true);
      setIsLongPressing(false);
    }, 500); // 500ms for mobile long-press
  };

  const handleTouchMove = (e) => {
    // If there's an active long-press timer, check if we've moved enough to cancel it
    if (longPressTimer.current && touchStart.current) {
      const touch = e.touches ? e.touches[0] : e;
      const deltaX = Math.abs(touch.clientX - touchStart.current.x);
      const deltaY = Math.abs(touch.clientY - touchStart.current.y);

      // Cancel long-press if finger moved more than 10px (indicates scrolling)
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        setIsLongPressing(false);
      }
    }
  };

  const handleLongPressEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsLongPressing(false);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();

    // Check if this was a scroll gesture (touch moved more than 10px or was quick)
    if (touchStart.current) {
      const touch = e.changedTouches ? e.changedTouches[0] : e;
      const deltaX = Math.abs(touch.clientX - touchStart.current.x);
      const deltaY = Math.abs(touch.clientY - touchStart.current.y);
      const touchDuration = Date.now() - touchStart.current.time;

      // Ignore if finger moved more than 10px (was scrolling)
      if (deltaX > 10 || deltaY > 10) {
        return;
      }

      // Ignore if touch was very quick (< 50ms) as it's likely scroll momentum
      if (touchDuration < 50) {
        return;
      }
    }

    toggleFavorite(quote.id);
  };

  const handleCardClick = (e) => {
    // Don't toggle if clicking on interactive elements, editing, or during long press
    if (e.target.closest('a, button, form, textarea, input') || isLongPressing || isEditing) {
      return;
    }

    // In Saves tab (viewMode=null) or Batch mode: toggle staging tray
    // In Manage mode: do nothing (editing is done via hover)
    if (viewMode === null || viewMode === 'batch') {
      if (inTray) {
        removeItem(quote);
      } else {
        addItem(quote);
      }
    }
  };

  const handleSkip = () => {
    setIsSkipModalOpen(true);
    setShowActions(false); // Close action overlay when opening modal
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this quote permanently?')) {
      try {
        await InkQuoteApi.deleteQuote(quote._inkquote_id);
        window.location.reload(); // Temporary - will improve in Priority 2
      } catch (error) {
        console.error('Failed to delete quote:', error);
        alert('Failed to delete quote. Please try again.');
      }
    }
  };

  return (
    <Card
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-200 mb-3 ${
        viewMode === 'batch' || viewMode === null ? 'cursor-pointer' : ''
      } ${
        inTray && (viewMode === 'batch' || viewMode === null)
          ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-900/20'
          : 'hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
      onClick={handleCardClick}
      onTouchStart={handleLongPressStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleLongPressEnd}
      onMouseDown={handleLongPressStart}
      onMouseMove={handleTouchMove}
      onMouseUp={handleLongPressEnd}
      onMouseEnter={() => viewMode === 'manage' && setShowActions(true)}
      onMouseLeave={(e) => {
        handleLongPressEnd(e);
        if (viewMode === 'manage') setShowActions(false);
      }}
    >
      <CardContent className="p-0 relative">
        <div className="flex">
          {/* Roam-style bullet point */}
          <div className="flex flex-col items-center pt-7 px-3 pb-11 pr-0">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-600 dark:bg-purple-400"></div>
            <div className="w-px flex-1 bg-gray-300 dark:bg-gray-600 mt-1"></div>
          </div>

          {/* Main content */}
          <div className="flex-1 p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 roam-header-top">
              <div className="flex items-center space-x-3">
                <div className="roam-page-title-container">
                  <div
                  ref={titleRef}
                  className={`roam-page-title-text text-blue-600 dark:text-blue-400 font-semibold ${viewMode !== null ? 'px-2 py-0.5 -mx-2 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''}`}
                  onClick={viewMode !== null && !isEditing ? (e) => {
                    if (!baseSourceUrl) {
                      e.stopPropagation();
                      setIsEditing(true);
                    }
                  } : undefined}
                  title={viewMode !== null && !baseSourceUrl ? "Click to edit page name" : undefined}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedPageTitle}
                        onChange={(e) => setEditedPageTitle(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Page name"
                        className="inline-block px-2 py-0.5 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    ) : baseSourceUrl ? (
                      <a
                        href={baseSourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 font-semibold"
                        title="Open block in Roam"
                      >
                        [[{pageTitle || 'Roam Quote'}]]
                      </a>
                    ) : (
                      <>[[{pageTitle || 'Roam Quote'}]]</>
                    )}
                  </div>

                  <div className="text-gray-600 dark:text-gray-400 text-sm">
                    by {isEditing ? (
                      <input
                        type="text"
                        value={editedAuthor}
                        onChange={(e) => setEditedAuthor(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block px-2 py-0.5 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    ) : (
                      <span className={viewMode !== null ? 'px-2 py-0.5 -mx-2 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''}
                        onClick={viewMode !== null && !isEditing ? (e) => {
                          e.stopPropagation();
                          setIsEditing(true);
                        } : undefined}
                        title={viewMode !== null ? "Click to edit author" : undefined}>
                        {displayAuthor}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 roam-date-star">
                <span className="tweet-date">
                  {formatDate(actualCreatedAt)}
                </span>
                <FavoriteStar itemId={quote.id} size={16} inline />
              </div>
            </div>

            {/* Quote content */}
            {isEditing ? (
              <div className="mb-3" onClick={e => e.stopPropagation()}>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows={6}
                />

                {/* Dev Info */}
                <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      ID: <span className="text-gray-900 dark:text-white">{quote._inkquote_id}</span>
                    </span>
                    <a
                      href={`https://api.treasurechest.design/inkquote/quotes/${quote._inkquote_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={12} />
                      API Endpoint
                    </a>
                  </div>
                </div>

                <div className="mt-2 flex gap-2">
                  <button
                    onClick={handleSave}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
                  >
                    <Save size={16} /> Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
                  >
                    <X size={16} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`mb-3 text-gray-900 dark:text-gray-100 leading-relaxed ${viewMode !== null ? 'px-2 py-1 -mx-2 -my-1 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''}`}
                onClick={viewMode !== null && !isEditing ? (e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                } : undefined}
                title={viewMode !== null ? "Click to edit content" : undefined}
              >
                {renderQuoteText(quoteContent)}
              </div>
            )}

            {/* Footer with source link and action buttons */}
            <div className="mt-4 flex justify-between items-center tweet-actions">
              {/* Left side - empty in Saves tab, filled in InkQuote tab */}
              <div className="flex items-center gap-4">
                {/* Favorite Star Button - Only in InkQuote tab */}
                {viewMode !== null && (
                  <button
                    onTouchStart={(e) => {
                      const touch = e.touches[0];
                      touchStart.current = {
                        x: touch.clientX,
                        y: touch.clientY,
                        time: Date.now()
                      };
                    }}
                    onClick={handleFavoriteClick}
                    className="tweet-action-button"
                    title="Toggle Favorite"
                  >
                    <Star
                      size={18}
                      className={isFavorite(quote.id) ? 'text-yellow-400 fill-yellow-400' : ''}
                    />
                  </button>
                )}

                {/* Tag Button - Only in InkQuote tab */}
                {viewMode !== null && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsTagModalOpen(true);
                    }}
                    className="tweet-action-button"
                    title="Manage Tags"
                  >
                    <Tag size={18} />
                  </button>
                )}

                {/* Skip Button - Only in InkQuote tab */}
                {viewMode !== null && (
                  <SkipCategoriesButton tweetId={quote.id} tweetSource="inkquote_roam" />
                )}
              </div>

              {/* Right side - Roam icon always on the right */}
              <a
                href={blockUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="tweet-action-button"
                title="Open block in Roam"
              >
                <RoamIcon size={18} color="currentColor" />
              </a>
            </div>
          </div>
        </div>

      </CardContent>

      {/* Skip Categories Modal */}
      {isSkipModalOpen && (
        <SkipCategoriesModal
          tweetId={quote.id}
          tweetSource="inkquote_roam"
          isOpen={isSkipModalOpen}
          onClose={() => setIsSkipModalOpen(false)}
        />
      )}

      {/* Tag Manager Modal */}
      {isTagModalOpen && (
        <TagManager
          quoteId={quote._inkquote_id}
          currentTags={quoteTags}
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          onTagsUpdated={(newTags) => {
            setQuoteTags(newTags);
            // Optionally reload the quote to get fresh data
            if (onUpdateQuote) {
              onUpdateQuote(quote._inkquote_id, {});
            }
          }}
        />
      )}
    </Card>
  );
};

export default RoamQuoteCard;